import BpkButton, {
  SIZE_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-button';
import BpkPanel from '@skyscanner-internal/backpack-web/bpk-component-panel';
import BpkSectionHeader from '@skyscanner-internal/backpack-web/bpk-component-section-header';
import classNames from 'classnames';

import Snippet from '../InfoSnippetDecoupled/Snippet';
import { useMBDContext } from '../common/MBD/useMBDContext';

import type {
  PanelLabel,
  PanelValue,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/BestTimeToVisitMonthPickerProps';

import STYLES from './InfoSnippetPanel.module.scss';

export type InfoSnippetPanelProps = {
  ctaText: string;
  panelLabels: PanelLabel;
  panelValue: PanelValue;
  panelIndex: number;
  isCompactCard: boolean;
  isRelFollow?: boolean;
  hideBottomSnippets?: boolean;
  hideCtaButton?: boolean;
};

const InfoSnippetPanel = ({
  ctaText,
  hideBottomSnippets = false,
  hideCtaButton = false,
  isCompactCard,
  isRelFollow = false,
  panelIndex,
  panelLabels,
  panelValue,
}: InfoSnippetPanelProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const mbdComponentName = `${rawComponentName}_pnl`;

  const { flightsLabel, hotelsLabel, rainfallLabel, temperatureLabel } =
    panelLabels;

  const panelClassName = classNames(STYLES.InfoSnippetPanel__outerPanel, {
    [STYLES['InfoSnippetPanel__outerPanel--default']]: isCompactCard === false,
    [STYLES['InfoSnippetPanel__outerPanel--compact']]: isCompactCard,
  });
  const {
    ctaLink,
    flightsLink,
    flightsValue,
    hotelsLink,
    hotelsValue,
    isShowTemperatureCategory,
    monthName,
    rainfallCategory,
    rainfallIcon,
    rainfallValue,
    temperatureCategory,
    temperatureValue,
  } = panelValue;

  const weatherSnippet = isShowTemperatureCategory ? (
    <Snippet
      icon="temperature"
      label={temperatureLabel}
      value={temperatureCategory}
      valueSecondLine={temperatureValue}
      snippetId={`${panelIndex}_temperature`}
      mbdParams={{ snippet_type: 'temperature' }}
    />
  ) : (
    <Snippet
      icon="temperature"
      label={temperatureLabel}
      value={temperatureValue}
      snippetId={`${panelIndex}_temperature`}
      mbdParams={{ snippet_type: 'temperature' }}
    />
  );
  const rainfallSnippet = (
    <Snippet
      icon={rainfallIcon}
      label={rainfallLabel}
      value={rainfallCategory}
      valueSecondLine={rainfallValue}
      snippetId={`${panelIndex}_rainfall`}
      mbdParams={{ snippet_type: 'rainfall' }}
    />
  );

  const flightTakeoffSnippet = (
    <Snippet
      icon="flightTakeoff"
      label={flightsLabel}
      value={flightsValue}
      linkUrl={flightsLink}
      snippetId={`${panelIndex}_cheapest_flight`}
      mbdParams={{ snippet_type: 'cheapest_flight' }}
    />
  );

  const hotelsSnippet = (
    <Snippet
      icon="hotels"
      label={hotelsLabel}
      value={hotelsValue}
      linkUrl={hotelsLink}
      snippetId={`${panelIndex}_cheapest_hotel`}
      mbdParams={{ snippet_type: 'cheapest_hotel' }}
    />
  );

  return (
    <div className={STYLES.InfoSnippetPanel}>
      <BpkPanel className={panelClassName} padded={false}>
        <div className={STYLES.InfoSnippetPanel__panel}>
          <div className={STYLES.InfoSnippetPanel__header}>
            <BpkSectionHeader title={monthName} />
          </div>
          <div className={STYLES.InfoSnippetPanel__snippetGrid}>
            <div>{weatherSnippet}</div>
            <div>{rainfallSnippet}</div>
            {!hideBottomSnippets && flightsValue && (
              <div>{flightTakeoffSnippet}</div>
            )}
            {!hideBottomSnippets && hotelsValue && <div>{hotelsSnippet}</div>}
          </div>
          {!hideCtaButton && (
            <BpkButton
              href={ctaLink}
              size={SIZE_TYPES.default}
              rel={isRelFollow ? '' : 'nofollow'}
              data-tracking-element-id={`${mbdComponentName}_${panelIndex}_search`}
              data-tracking-common-params={buildMBDParam(
                `${mbdComponentName}_search`,
              )}
            >
              {ctaText}
            </BpkButton>
          )}
        </div>
      </BpkPanel>
    </div>
  );
};

export default InfoSnippetPanel;
